import { useEffect, useState } from "react";
import { EquiroCalendar } from "./EquiroCalendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { CalendarIcon } from "lucide-react";

export const toDisplayIT = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return (y && m && d) ? `${d}/${m}/${y}` : "";
};

const toISO = (dt) => {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Parse a manually typed "dd/mm/yyyy" (or ddmmyyyy). Returns ISO or null when invalid.
const parseIT = (text) => {
  const digits = text.replace(/[^0-9]/g, "");
  if (digits.length !== 8) return null;
  const dd = digits.slice(0, 2), mm = digits.slice(2, 4), yy = digits.slice(4, 8);
  const dt = new Date(`${yy}-${mm}-${dd}T00:00:00`);
  if (isNaN(dt.getTime())) return null;
  if (dt.getDate() !== Number(dd) || dt.getMonth() + 1 !== Number(mm)) return null;
  return `${yy}-${mm}-${dd}`;
};

// Italian date field (dd/mm/yyyy) that stores/returns an ISO yyyy-mm-dd string.
// V2: supports manual typing + quick month/year dropdowns in the calendar.
export function DateField({ value, onChange, placeholder = "gg/mm/aaaa", className = "",
                            testId, disabled, fromYear, toYear }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(toDisplayIT(value));
  useEffect(() => { setText(toDisplayIT(value)); }, [value]);

  const selected = value ? new Date(String(value).slice(0, 10) + "T00:00:00") : undefined;
  const cy = new Date().getFullYear();
  const fy = fromYear ?? (cy - 40);
  const ty = toYear ?? (cy + 10);

  // When nested inside a Radix Dialog, closing a modal popover on iOS can leave
  // `pointer-events: none` on <body>, freezing the parent dialog. Reset it on close.
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => { document.body.style.pointerEvents = ""; }, 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  const commit = () => {
    if (text.trim() === "") { onChange(""); return; }
    const iso = parseIT(text);
    if (iso) onChange(iso);
    else setText(toDisplayIT(value)); // revert invalid input
  };

  return (
    <div className={`relative ${className}`}>
      <input
        type="text" inputMode="numeric" disabled={disabled} value={text} placeholder={placeholder}
        data-testid={testId ? `${testId}-text` : undefined}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      />
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} data-testid={testId} aria-label="Apri calendario"
            className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-md text-[#8C532B] hover:bg-[#F0EAE1] disabled:opacity-50">
            <CalendarIcon className="h-4 w-4 opacity-70" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 pointer-events-auto" align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}>
          <EquiroCalendar mode="single" selected={selected} defaultMonth={selected}
            fromYear={fy} toYear={ty}
            onSelect={(d) => { onChange(d ? toISO(d) : ""); setOpen(false); }} />
        </PopoverContent>
      </Popover>
    </div>
  );
}
